'use client'

import {getTableOfContentSections} from '~/cms/helpers/getTableOfContentSections'
import {grid} from '~/design-system/foundations'
import {HgArticleFootnotes, type HgTagProps} from '~/design-system/hg/components'
import HgArticleCredits, {
  type HgArticleCreditsProps,
} from '~/design-system/hg/components/HgArticleCredits'
import HgArticleNotes, {
  type HgArticleNotesProps,
} from '~/design-system/hg/components/HgArticleNotes'
import HgTableOfContents from '~/design-system/hg/components/HgTableOfContents'
import {cn} from '~/design-system/utils'
import {SingleLineStructuredText} from '../../SingleLineStructuredText'
import {type SingleLineStructuredTextFragment} from '../../SingleLineStructuredText/_generated/fragment.graphql'
import ReturnToTableOfContentsButton, {
  TEXT_CONTENT_ID,
} from '../../WYSIWYG/_components/ReturnToTableOfContentsButton'
import {ArticleWYSIWYG, type ArticleWYSIWYGProps} from '../ArticleWYSIWYG'

type ArticleContentProps = {
  data?: ArticleWYSIWYGProps['data']
  showTableOfContents?: boolean
  footnotes?: SingleLineStructuredTextFragment | null
  tags: HgTagProps[]
  authors: HgArticleNotesProps['authors']
} & Omit<HgArticleCreditsProps, 'authors'>

export default function ArticleContent({
  data,
  showTableOfContents,
  footnotes,
  dateUpdated,
  articleTitle,
  assetCredits,
  authors,
  datePublished,
  tags,
}: ArticleContentProps) {
  if (!data) return null
  const sections = getTableOfContentSections(data.article)

  return (
    <>
      {showTableOfContents && <HgTableOfContents sections={sections} />}

      <div className={grid({className: 'm-auto'})}>
        <hr
          className={cn(
            'col-span-full mt-s8 border-t border-border-frosted pt-20 md:col-span-8 md:col-start-3 lg:col-span-full'
          )}
        />
      </div>

      <div id={TEXT_CONTENT_ID} className={grid({className: 'm-auto'})}>
        <HgArticleCredits
          authors={authors}
          assetCredits={assetCredits}
          datePublished={datePublished}
          dateUpdated={dateUpdated}
          articleTitle={articleTitle}
        />
        <ArticleWYSIWYG data={data} />
        {footnotes && (
          <HgArticleFootnotes text={<SingleLineStructuredText data={footnotes} />} />
        )}
        <HgArticleNotes authors={authors} title={articleTitle} tags={tags} />
      </div>
      {sections.length > 0 && showTableOfContents && (
        <ReturnToTableOfContentsButton />
      )}
    </>
  )
}
